import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-primary mb-3">{title}</h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-background font-manrope">
    <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Package className="w-8 h-8 text-accent" />
          <span className="text-xl font-bold text-primary">KAHA</span>
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button variant="ghost" asChild className="mb-6 gap-2" data-testid="btn-back-home">
        <Link to="/"><ArrowLeft className="w-4 h-4" /> Volver al inicio</Link>
      </Button>

      <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">Aviso de Privacidad</h1>
      <p className="text-sm text-muted-foreground mb-10">Última actualización: marzo 2026</p>

      <Section title="1. Responsable del tratamiento de datos">
        <p>KAHA Packaging (en adelante "KAHA"), con domicilio en León, Guanajuato, México, es responsable del tratamiento de los datos personales que usted nos proporcione, de conformidad con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).</p>
      </Section>

      <Section title="2. Datos personales que recopilamos">
        <p>Para procesar sus pedidos y brindarle nuestros servicios, podemos recopilar los siguientes datos personales:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Datos de identificación:</strong> nombre completo, razón social.</li>
          <li><strong>Datos de contacto:</strong> correo electrónico, número de teléfono.</li>
          <li><strong>Datos de facturación:</strong> RFC, régimen fiscal, código postal fiscal, dirección fiscal.</li>
          <li><strong>Datos de envío:</strong> dirección de entrega (calle, colonia, ciudad, estado, código postal).</li>
          <li><strong>Datos de navegación:</strong> dirección IP, tipo de navegador, páginas visitadas (recopilados automáticamente).</li>
        </ul>
      </Section>

      <Section title="3. Finalidades del tratamiento de datos">
        <p>Sus datos personales serán utilizados para las siguientes finalidades:</p>
        <p><strong>Finalidades primarias (necesarias para el servicio):</strong></p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Procesar y gestionar sus pedidos de cajas personalizadas.</li>
          <li>Emitir cotizaciones y confirmaciones de compra.</li>
          <li>Coordinar la entrega o envío de sus productos.</li>
          <li>Emitir comprobantes fiscales (CFDI) cuando lo solicite.</li>
          <li>Comunicarnos con usted sobre el estado de su pedido.</li>
          <li>Atender solicitudes de soporte y servicio al cliente.</li>
        </ul>
        <p><strong>Finalidades secundarias (no necesarias pero útiles):</strong></p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Enviar información sobre nuevos productos o promociones.</li>
          <li>Mejorar la experiencia del usuario en nuestra plataforma.</li>
        </ul>
        <p>Si no desea que sus datos sean tratados para las finalidades secundarias, puede indicarlo enviando un correo a <a href="mailto:hola@kahamx.com" className="text-accent hover:underline">hola@kahamx.com</a>.</p>
      </Section>

      <Section title="4. Compartición de datos con terceros">
        <p>Para cumplir con las finalidades descritas, podemos compartir sus datos personales con los siguientes terceros:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Mercado Pago:</strong> para el procesamiento seguro de pagos con tarjeta, transferencia y otros métodos.</li>
          <li><strong>Servicios de paquetería:</strong> para la coordinación y entrega de sus pedidos (nombre, dirección y teléfono de envío).</li>
          <li><strong>Google:</strong> para la autenticación de cuentas de usuario mediante Google Sign-In.</li>
        </ul>
        <p>Estos terceros están obligados a proteger sus datos de acuerdo con sus propias políticas de privacidad y la legislación aplicable.</p>
      </Section>

      <Section title="5. Protección de datos">
        <p>KAHA implementa medidas de seguridad administrativas, técnicas y físicas para proteger sus datos personales contra daño, pérdida, alteración, destrucción o uso no autorizado, incluyendo:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Conexiones cifradas (HTTPS/SSL) en toda la plataforma.</li>
          <li>Procesamiento de pagos delegado a plataformas certificadas (KAHA no almacena datos de tarjetas).</li>
          <li>Acceso restringido a datos personales solo al personal autorizado.</li>
          <li>Almacenamiento seguro en servidores con protección y respaldos periódicos.</li>
        </ul>
      </Section>

      <Section title="6. Derechos ARCO">
        <p>Usted tiene derecho a:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Acceder</strong> a sus datos personales en nuestro poder.</li>
          <li><strong>Rectificar</strong> sus datos cuando sean inexactos o estén incompletos.</li>
          <li><strong>Cancelar</strong> sus datos cuando considere que no son necesarios para las finalidades descritas.</li>
          <li><strong>Oponerse</strong> al tratamiento de sus datos para fines específicos.</li>
        </ul>
        <p>Para ejercer cualquiera de estos derechos, envíe una solicitud por correo electrónico a <a href="mailto:hola@kahamx.com" className="text-accent hover:underline">hola@kahamx.com</a> con el asunto "Derechos ARCO", indicando su nombre completo, el derecho que desea ejercer y una breve descripción de su solicitud. Responderemos en un plazo máximo de 20 días hábiles.</p>
      </Section>

      <Section title="7. Uso de cookies">
        <p>Nuestro sitio web utiliza cookies y tecnologías similares para mejorar su experiencia de navegación, recordar sus preferencias y analizar el uso del sitio. Usted puede configurar su navegador para rechazar cookies, aunque esto podría limitar algunas funcionalidades.</p>
      </Section>

      <Section title="8. Cambios al aviso de privacidad">
        <p>KAHA se reserva el derecho de modificar el presente aviso de privacidad en cualquier momento. Las modificaciones serán publicadas en esta página con la fecha de actualización correspondiente. Le recomendamos revisar periódicamente este aviso.</p>
      </Section>

      <Section title="9. Contacto para asuntos de privacidad">
        <p>Si tiene preguntas o comentarios sobre este aviso de privacidad o el tratamiento de sus datos personales, puede contactarnos en:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Correo electrónico: <a href="mailto:hola@kahamx.com" className="text-accent hover:underline">hola@kahamx.com</a></li>
          <li>WhatsApp: <a href="https://wa.me/524778055212" className="text-accent hover:underline">+52 477 805 5212</a></li>
        </ul>
      </Section>
    </main>

    <footer className="py-8 bg-white border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} KAHA Packaging. Todos los derechos reservados.</p>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;
